import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Field, FilePicker, InfoTip, inputCls } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation, FieldInvalidInline } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import GroupedEngagementPicker from '../components/workflow/GroupedEngagementPicker';
import { MultiStaffPicker } from '../components/shared/EngagementFormFields';
import { IndependenceDeclaration } from '../components/workflow/IndependenceDeclaration';
import EngagementRoster from '../components/workflow/EngagementRoster';
import { fetchJSON } from '../utils/api';
import { engagementTag } from '../utils/engagementName';

// CaseWare, Charge Code & Team Update — submission form (docs/modules/workflow-cwcc.md).
// Pick the engagement(s) — one submission may cover several companies of the
// same client group + FYE year — then tick what you need: CaseWare access, a charge
// code and/or a team member update (the FULL replacement roster). A holder of
// the review key approves the whole request; QM then performs each item in the
// external systems and marks it completed — only that completion writes the
// engagement flags and team. A CW/CC side any selected engagement already has
// is blocked; the team side is always available.
const WorkflowCwccNew = () => {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState([]);
  const [staff, setStaff] = useState([]);
  const [engagementIds, setEngagementIds] = useState([]);
  const [needCw, setNeedCw] = useState(false);
  const [needCc, setNeedCc] = useState(false);
  const [needMember, setNeedMember] = useState(false);
  const [teamStaffIds, setTeamStaffIds] = useState([]);
  const [teamSeeded, setTeamSeeded] = useState(false);
  const [independence, setIndependence] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/cwcc/options');
        setEngagements(data.engagements || []);
        setStaff(data.staff || []);
      } catch (e) { setError(e.message); }
      setLoaded(true);
    })();
  }, []);

  const selected = useMemo(
    () => engagements.filter((e) => engagementIds.includes(e.id)),
    [engagements, engagementIds]
  );
  const cwBlocked = selected.some((e) => e.casewareOpened);
  const ccBlocked = selected.some((e) => !!e.chargeCodeAssigned);
  // 後加選的案讓某一側變成「已滿足」時，把勾過的那一側收回——否則送出必 400。
  useEffect(() => { if (cwBlocked) setNeedCw(false); }, [cwBlocked]);
  useEffect(() => { if (ccBlocked) setNeedCc(false); }, [ccBlocked]);

  // 勾了 team 側就把第一個涵蓋案的現任 team 帶進來當底稿（只帶一次，
  // 之後由填單人自己加減）。
  useEffect(() => {
    if (!needMember || teamSeeded) return;
    const first = selected[0];
    const current = ((first && first.teamMembers) || []).map((m) => m.staffId).filter(Boolean);
    if (current.length > 0) setTeamStaffIds(current);
    setTeamSeeded(true);
  }, [needMember, teamSeeded, selected]);

  // Everything still empty, keyed to the control it belongs to — after the
  // first Submit the page marks those controls and lists them in the banner.
  const gaps = useMemo(() => {
    const g = [];
    if (engagementIds.length === 0) g.push({ key: 'engagementIds', label: 'Engagement(s)' });
    if (!needCw && !needCc && !needMember) g.push({ key: 'requestedSides', label: 'CaseWare Access / Charge Code / Team Member Update' });
    if (needMember && teamStaffIds.length === 0) g.push({ key: 'teamStaffIds', label: 'Team Members (At Least One)' });
    if (!independence) g.push({ key: 'independenceDeclared', label: 'Independence Declaration' });
    return g;
  }, [engagementIds, needCw, needCc, needMember, teamStaffIds, independence]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true); setError(null);
    try {
      const form = new FormData();
      form.set('engagementIds', JSON.stringify(engagementIds));
      form.set('needCw', needCw ? 'true' : 'false');
      form.set('needCc', needCc ? 'true' : 'false');
      form.set('needMember', needMember ? 'true' : 'false');
      if (needMember) form.set('teamStaffIds', JSON.stringify(teamStaffIds));
      form.set('independenceDeclared', independence ? 'true' : 'false');
      form.set('remarks', remarks);
      files.forEach((f) => form.append('files', f));
      const data = await fetchJSON('/api/workflow/cwcc', { method: 'POST', body: form });
      navigate(`/workflow/cwcc/${data.request.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/cwcc"
      backLabel="CaseWare, Charge Code & Team Update"
      title="New CaseWare, Charge Code & Team Update Request"
      subtitle="Request CaseWare access, a charge code or a team update."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the Engagement(s)" hint={<>Your engagements, grouped by client group and financial year. <InfoTip text="One submission may cover several companies of the same client group whose FYE falls in the same year — the year-end dates may differ. Engagements that already have CaseWare access and a charge code stay listed — their team can still be updated." /></>}>
          <GroupedEngagementPicker
            engagements={engagements}
            selectedIds={engagementIds}
            onChange={(ids) => { setEngagementIds(ids); setError(null); }}
            renderExtra={(e) => (
              <>
                <p className="text-xs text-slate-500">
                  {[e.entityName || e.engagementName, engagementTag(e.serviceProvided, e.engagementSeq, e.serviceDescription)].filter(Boolean).join(' · ')} — CaseWare: {e.casewareOpened ? 'already activated' : 'not activated'} ·
                  {' '}Charge code: {e.chargeCodeAssigned ? 'already activated' : 'not activated'}
                </p>
                {engagementIds.includes(e.id) && <EngagementRoster engagement={e} className="mt-1" />}
              </>
            )}
          />
          <FieldInvalidInline field="engagementIds" text="Required — pick at least one engagement" />
          {loaded && engagements.length === 0 && (
            <p className="text-xs text-amber-600">
              No engagement to request for — you are not a member of any open engagement.
            </p>
          )}
        </FormSection>

        <FormSection step={2} title="You Are Requesting For">
          <div className="space-y-2">
            <label className={`flex items-center gap-2 text-sm ${cwBlocked ? 'text-slate-400' : 'text-slate-700'}`}>
              <input type="checkbox" checked={needCw} disabled={cwBlocked} onChange={(e) => setNeedCw(e.target.checked)} />
              CaseWare Access {cwBlocked && <span className="text-xs">(already activated on a selected engagement)</span>}
            </label>
            <label className={`flex items-center gap-2 text-sm ${ccBlocked ? 'text-slate-400' : 'text-slate-700'}`}>
              <input type="checkbox" checked={needCc} disabled={ccBlocked} onChange={(e) => setNeedCc(e.target.checked)} />
              Charge Code {ccBlocked && <span className="text-xs">(already activated on a selected engagement)</span>}
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={needMember} onChange={(e) => setNeedMember(e.target.checked)} />
              Team Member Update
            </label>
          </div>
          <FieldInvalidInline field="requestedSides" text="Required — tick at least one item" />

          {needMember && (
            <Field label="Requested Team Members" required field="teamStaffIds"
              hint="The full team after the update, applied to every covered engagement when QM completes the item. The current team of the first selected engagement is pre-filled.">
              <MultiStaffPicker
                options={staff}
                known={selected.flatMap((e) => e.teamMembers || [])}
                selectedIds={teamStaffIds}
                onChange={setTeamStaffIds}
                placeholder="Add a team member…"
              />
            </Field>
          )}
        </FormSection>

        <FormSection step={3} title="Attachments">
          <Field label="Supporting Files (e.g. Booking Form)">
            <FilePicker multiple kind="docsAndImages"
              onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </Field>
        </FormSection>

        <FormSection step={4} title="Declaration & Remarks">
          <Field label="Independence Declaration (applies to all persons on the engagement)" required field="independenceDeclared">
            <IndependenceDeclaration
              checked={independence}
              onChange={setIndependence}
              confirmLabel="I confirm all of the above on behalf of every person named on this request."
            />
          </Field>
          <Field label="Remarks">
            <textarea rows="2" value={remarks} onChange={(e) => setRemarks(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowCwccNew;
